import { SnapPick, SnapType, Vector3 } from '@technology/web3d';
import { ImagePosition, Markup } from '@technology/web3d-plugin-markup';
import type { TextureType } from '@trimble/types';
import cancelIcon from '../assets/markup-icons/cancel.png';
import errorIcon from '../assets/markup-icons/error.png';
import flagIcon from '../assets/markup-icons/flag.png';
import locationIcon from '../assets/markup-icons/location.png';
import pinIcon from '../assets/markup-icons/pin.png';
import pointIcon from '../assets/markup-icons/point.png';
import starIcon from '../assets/markup-icons/star.png';
import warningIcon from '../assets/markup-icons/warning.png';

const PERCENTAGE_MULTIPLIER = 100;
const RATIO_PRECISION_MULTIPLIER = 1000;

// Base measurement properties that all measurements share
export interface BaseMeasurement {
  id: string;
  // Markup array (one for each viewer).
  markups: Markup[];
  // (RGB values 0-255)
  color?: {
    r: number;
    g: number;
    b: number;
    a?: number; // Optional alpha channel for transparency (0-255)
  };
  hidden?: boolean;
  savedStatus?: MeasurementSaveStatus;
}

export type MeasurementSaveStatus =
  | 'save_failed'
  | 'saving_in_progress'
  | 'save_successful';

// Specific measurement types
export interface PointMeasurementType extends BaseMeasurement {
  type: 'point';
  x: number;
  y: number;
  z: number;
  label?: string;
  showXYCoordinates?: boolean;
  showZCoordinate?: boolean;
  pointSymbol?: PointSymbol;
}

export interface LineMeasurementType extends BaseMeasurement {
  type: 'line';
  startPoint: Vector3;
  endPoint: Vector3;
  value: number;
  label?: string;
}

export interface AngleMeasurementType extends BaseMeasurement {
  type: 'angle';
  startPoint: Vector3;
  endPoint: Vector3;
  middlePoint: Vector3;
  value: number;
  snapStart: SnapType;
  snapEnd: SnapType;
  label?: string;
}

export interface AlignmentMeasurementType extends BaseMeasurement {
  type: 'alignment';
  value: number;
  ground: Vector3;
  point: Vector3;
  alignment: Vector3;
  label?: string;
}

export interface PolylineMeasurementType extends BaseMeasurement {
  type: 'polyline';
  value: number;
  picks: SnapPick[];
  label?: string;
  showSegmentDistances: boolean;
  showSegmentSlopes: boolean;
  showHorizontalMeasurements: boolean;
  showVerticalMeasurements: boolean;
  slopeFormat?: SlopeFormat;
}

export interface AreaMeasurementType
  extends BaseMeasurement,
    PolygonMeasurementSettings {
  type: 'area';
  picks: SnapPick[];
  label?: string;
}

export enum LineStyle {
  Solid = 'solid',
  Dashed = 'dashed',
  Dotted = 'dotted',
}

type VolumeType = 'SurfaceToSurface';

export interface VolumeMeasurementParameters {
  id?: string;
  volumeType?: VolumeType | null;
  projectUid?: string | null;
  accountUid?: string | null;
  fromSurfaceId?: string | null;
  fromSurfaceName?: string | null;
  toSurfaceId?: string | null;
  toSurfaceName?: string | null;
  boundaryInclusive?: {
    [key: string]: unknown;
  } | null;
  tilesetUrl?: string | null;
}

export interface VolumeMeasurementResults {
  netVolume?: number | null;
  totalVolume?: number | null;
  totalCutVolume?: number | null;
  totalFillVolume?: number | null;
  totalBalanceVolume?: number | null;
  totalCutArea?: number | null;
  totalFillArea?: number | null;
  totalCoverageArea?: number | null;
  tilesetUrl?: string | null;
}

/** Default opacity for volume heatmap tilesets (1 = fully opaque). */
export const DEFAULT_VOLUME_TILESET_OPACITY = 1;

export interface VolumeMeasurementType extends BaseMeasurement {
  type: 'volume';
  label?: string;
  parameters: VolumeMeasurementParameters;
  result?: VolumeMeasurementResults;
  paletteId?: string;
  textureType?: TextureType;
  /** Opacity of the volume heatmap tileset in the viewer (0–1). */
  fillOpacity?: number;
  lineThickness?: number;
  lineStyle?: LineStyle;
}

export interface PointSymbol {
  name: string;
  url: string;
  position: ImagePosition;
}

export const pointSymbols: PointSymbol[] = [
  { name: 'location', position: ImagePosition.Top, url: locationIcon },
  { name: 'point', position: ImagePosition.Center, url: pointIcon },
  { name: 'error', position: ImagePosition.Center, url: errorIcon },
  { name: 'warning', position: ImagePosition.Center, url: warningIcon },
  { name: 'cancel', position: ImagePosition.Center, url: cancelIcon },
  { name: 'pin', position: ImagePosition.Top, url: pinIcon },
  { name: 'flag', position: ImagePosition.Top, url: flagIcon },
  { name: 'star', position: ImagePosition.Center, url: starIcon },
];

export interface MeasurementInfoButtonConfig {
  action(): void;
  tooltip?: string;
}

// Union type for all measurements
export type Measurement =
  | PointMeasurementType
  | LineMeasurementType
  | AngleMeasurementType
  | AlignmentMeasurementType
  | PolylineMeasurementType
  | AreaMeasurementType
  | VolumeMeasurementType;

export type MeasurementType = Measurement['type'];

// Type guards for easier usage
export const isPointMeasurement = (
  measurement: Measurement
): measurement is PointMeasurementType => {
  return measurement.type === 'point';
};

export const isLineMeasurement = (
  measurement: Measurement
): measurement is LineMeasurementType => {
  return measurement.type === 'line';
};

export const isAngleMeasurement = (
  measurement: Measurement
): measurement is AngleMeasurementType => {
  return measurement.type === 'angle';
};

export const isAlignmentMeasurement = (
  measurement: Measurement
): measurement is AlignmentMeasurementType => {
  return measurement.type === 'alignment';
};

export const isPolylineMeasurement = (
  measurement: Measurement
): measurement is PolylineMeasurementType => {
  return measurement.type === 'polyline';
};

export const isVolumeMeasurement = (
  measurement: Measurement
): measurement is VolumeMeasurementType => {
  return measurement.type === 'volume';
};

export const isAreaMeasurement = (
  measurement: Measurement
): measurement is AreaMeasurementType => {
  return measurement.type === 'area';
};

export interface PolylineMeasurementSettings {
  showSegmentDistances: boolean;
  showSegmentSlopes: boolean;
  showHorizontalMeasurements: boolean;
  showVerticalMeasurements: boolean;
}

export interface PointMeasurementSettings {
  showXYCoordinates: boolean;
  showZCoordinate: boolean;
}

// eslint-disable-next-line no-magic-numbers
export const lineThicknessOptions: number[] = [1, 2, 3, 4, 8, 12, 16];

export interface PolygonMeasurementSettings {
  lineThickness: number;
  lineStyle: LineStyle;
  fillOpacity: number;
}

export const DEFAULT_POLYGON_MEASUREMENT_SETTINGS: Readonly<PolygonMeasurementSettings> =
  {
    lineThickness: 2,
    lineStyle: LineStyle.Solid,
    fillOpacity: 0.1,
  };

export type SlopeFormat = 'ratio' | 'percentage' | 'degrees';

export function formatSlopeAngleAsPercentage(
  vertical: number,
  horizontal: number
): string {
  return `-${((PERCENTAGE_MULTIPLIER * vertical) / horizontal).toFixed(1)} %`;
}

export function formatSlopeAngleAsRatio(
  vertical: number,
  horizontal: number
): string {
  let ratio = horizontal / vertical;
  if (isNaN(ratio)) {
    return 'invalid';
  }
  if (vertical === 0) {
    return '1:∞';
  }

  ratio =
    Math.round(ratio * RATIO_PRECISION_MULTIPLIER) / RATIO_PRECISION_MULTIPLIER;
  return `1 : ${ratio.toString()}`;
}
